/**
 * Report and block — App Store Guideline 1.2.
 *
 * A block is between the startup and the investor profile (see
 * 20260916120000_ugc_block_report_filter.sql), not between two users — a
 * co-founder must be able to see (and lift) a block their teammate placed.
 * So every hook here takes the PAIR ({ startupId, investorProfileId }), and
 * `usePairBlock` reports which SIDE placed the block rather than which user.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase, isSupabaseConfigured } from '@/lib/supabase/client';
import { useAuth } from '@/lib/supabase/auth';

export type BlockSide = 'startup' | 'investor';
export interface Pair {
  startupId: string;
  investorProfileId: string;
}
export type ReportTarget = 'message' | 'conversation' | 'review';
export type ReportReason = 'spam' | 'harassment' | 'inappropriate' | 'other';

export interface PairBlockState {
  blockingSide: BlockSide;
  /** Did MY side place this block? Drives the composer copy and which action (Block vs Unblock) shows. */
  blockedByMySide: boolean;
}

const blockKey = (pair: Pair | null) =>
  ['pair-block', pair?.startupId ?? null, pair?.investorProfileId ?? null] as const;

/** True for the Postgres error the objectionable-text filter trigger raises (P0001, message = 'objectionable_content'). */
export function isObjectionableContentError(err: unknown): boolean {
  return typeof err === 'object' && err !== null && (err as { message?: unknown }).message === 'objectionable_content';
}

/**
 * Reads whether a pair is blocked, and if so, from which side.
 *
 * `side` is the CALLER's side (the chat screen's `currentUser.type`) — needed
 * only to compute `blockedByMySide` client-side; the query itself is
 * side-independent so it caches per pair regardless of who's asking.
 */
export function usePairBlock(pair: Pair | null, side: BlockSide | null) {
  const { data, isLoading } = useQuery({
    queryKey: blockKey(pair),
    enabled: !!pair && isSupabaseConfigured(),
    queryFn: async (): Promise<{ blockingSide: BlockSide } | null> => {
      if (!pair) return null;
      const { data: row, error } = await supabase
        .from('conversation_blocks')
        .select('blocking_side')
        .eq('startup_id', pair.startupId)
        .eq('investor_profile_id', pair.investorProfileId)
        .maybeSingle();
      if (error) throw error;
      return row ? { blockingSide: row.blocking_side as BlockSide } : null;
    },
  });

  const block: PairBlockState | null = data
    ? { blockingSide: data.blockingSide, blockedByMySide: side != null && data.blockingSide === side }
    : null;

  return { block, isLoading };
}

export function useBlockPair() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ startupId, investorProfileId, side }: Pair & { side: BlockSide }) => {
      if (!user) throw new Error('Cannot block without a signed-in user');
      const { error } = await supabase.from('conversation_blocks').insert({
        startup_id: startupId,
        investor_profile_id: investorProfileId,
        blocked_by_user_id: user.id,
        blocking_side: side,
      });
      if (error) throw error;
    },
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: blockKey(v) }),
  });
}

/**
 * Deletes the block row for a pair. Which side is allowed to do this is an
 * RLS decision ("Blocks: a party lifts a block for its own side") — the
 * client doesn't need to (and per the addendum, can't cleanly) pass a side.
 */
export function useUnblockPair() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ startupId, investorProfileId }: Pair) => {
      const { error } = await supabase
        .from('conversation_blocks')
        .delete()
        .eq('startup_id', startupId)
        .eq('investor_profile_id', investorProfileId);
      if (error) throw error;
    },
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: blockKey(v) }),
  });
}

export function useReportContent() {
  return useMutation({
    mutationFn: async (report: { targetType: ReportTarget; targetId: string; reason: ReportReason; details?: string }) => {
      const { error } = await supabase.functions.invoke('report-content', { body: report });
      if (error) throw error;
    },
  });
}
